import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IMAGES } from "../constants/theme";
import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";
import { DynamicLibraryModal } from "../components/PricingTable";

const FALLBACK_IMAGES = [
  IMAGES.services1,
  IMAGES.services2,
  IMAGES.services3,
  IMAGES.services4,
];

// ── Subscription Modal ────────────────────────────────────────────────────────

const SubscriptionModal = ({ library, onClose, onSubscribe, loading }) => {
  if (!library) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#1a1a1a",
          borderRadius: "12px",
          padding: "40px",
          maxWidth: "500px",
          width: "90%",
          color: "#fff",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "14px",
            right: "18px",
            background: "none",
            border: "none",
            color: "#aaa",
            fontSize: "22px",
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          &times;
        </button>

        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <i
            className="fa fa-lock"
            style={{ fontSize: "36px", color: "var(--primary)", marginBottom: "14px", display: "block" }}
          />
          <h4 style={{ marginBottom: "6px" }}>Subscribe to Access</h4>
          <p style={{ color: "#aaa", margin: 0 }}>
            Choose a package to unlock <strong>{library.name}</strong>
          </p>
        </div>

        {library.packages?.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {library.packages.map((pkg) => (
              <div
                key={pkg.id}
                style={{
                  border: "1px solid #333",
                  borderRadius: "8px",
                  padding: "16px 20px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div>
                  <div style={{ fontWeight: "600", marginBottom: "3px" }}>{pkg.name}</div>
                  <div style={{ color: "#aaa", fontSize: "13px" }}>{pkg.duration_days} days</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "14px", flexShrink: 0 }}>
                  <span style={{ fontWeight: "700", fontSize: "17px", color: "var(--primary)" }}>
                    {pkg.currency} {pkg.price}
                  </span>
                  <button
                    className="btn btn-primary btn-skew"
                    onClick={() => onSubscribe(pkg)}
                    disabled={loading}
                    style={{ minWidth: "110px" }}
                  >
                    <span>{loading ? "Please wait…" : "Subscribe"}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ textAlign: "center", color: "#aaa" }}>
            No packages available at the moment.
          </p>
        )}
      </div>
    </div>
  );
};

// ── Library Card ──────────────────────────────────────────────────────────────

const LibraryCard = ({ library, index, onOpen }) => {
  const isSecondary = index % 4 === 0 || index % 4 === 3;
  const bgClass  = isSecondary ? "bg-secondary" : "bg-primary";
  const textClass = isSecondary ? "text-white" : "theme-text-color";
  const btnClass  = isSecondary ? "btn-primary"  : "btn-secondary";
  const imageSrc = library.thumbnail_url || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];

  const infoBlock = (
    <div
      className="col-xl-3 col-md-6 hpa-library-info-column"
      style={{ "--hpa-mobile-order": index * 2 + 2 }}
    >
      <div className={`dz-info ${bgClass}`}>
        <div className={`clearfix ${textClass}`}>
          <span className={isSecondary ? "text-primary subtitle" : "subtitle"}>
            {library.slug?.toUpperCase().replace(/-/g, " ")}
          </span>
          <h4 className={`title ${textClass}`}>{library.name}</h4>
          <p>
            {library.description ||
              "Explore a structured exercise database organized for professional training and performance development."}
          </p>
          <button
            onClick={() => onOpen(library)}
            className={`btn ${btnClass} btn-skew`}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
          >
            {!library.has_access && (
              <i className="fa fa-lock" style={{ fontSize: "13px" }} />
            )}
            <span>Open Library</span>
          </button>
        </div>
      </div>
    </div>
  );

  const imageBlock = (
    <div
      className="col-xl-3 col-md-6 hpa-library-image-column"
      style={{ "--hpa-mobile-order": index * 2 + 1 }}
    >
      <div className="dz-media">
        <img src={imageSrc} alt={library.name} />
      </div>
    </div>
  );

  // Alternate: 0,1 → info-left / image-right  |  2,3 → image-left / info-right
  return index % 4 < 2 ? (
    <>
      {infoBlock}
      {imageBlock}
    </>
  ) : (
    <>
      {imageBlock}
      {infoBlock}
    </>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const FitnessTraining = () => {
  const [libraries, setLibraries]         = useState([]);
  const [fetching, setFetching]           = useState(true);
  const [subscribeModal, setSubscribeModal] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [dynamicPackage, setDynamicPackage] = useState(null);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState([]);
  const [paymentError, setPaymentError] = useState("");
  const { user } = useAuth();
  const navigate  = useNavigate();

  useEffect(() => {
    setFetching(true);
    apiClient
      .get("/libraries")
      .then((res) => setLibraries(res.data.data || []))
      .catch(() => setLibraries([]))
      .finally(() => setFetching(false));
  }, [user]); // re-fetch after login/logout so has_access refreshes

  const handleOpenLibrary = (library) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (!library.has_access) {
      setSubscribeModal(library);
      return;
    }
    navigate(`/library/${library.slug}`);
  };

  const handleSubscribe = async (pkg, selectedIds = []) => {
    if (pkg.is_dynamic && selectedIds.length === 0) {
      setSubscribeModal(null);
      setSelectedLibraryIds([]);
      setPaymentError("");
      setDynamicPackage(pkg);
      return;
    }

    setPaymentLoading(true);
    setPaymentError("");
    try {
      const res = await apiClient.post("/payments/initiate", {
        package_id: pkg.id,
        selected_library_ids: selectedIds,
      });
      const collectUrl = res.data.data?.collect_url;
      if (collectUrl) {
        window.location.href = collectUrl;
      }
    } catch (err) {
      console.error("Payment initiation failed:", err);
      setPaymentError(
        err.response?.data?.message
          || Object.values(err.response?.data?.errors || {})?.[0]?.[0]
          || "Could not prepare payment. Please try again.",
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  if (fetching) {
    return (
      <div
        className="row g-0"
        style={{ minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <p className="text-white">Loading libraries…</p>
      </div>
    );
  }

  return (
    <>
      <div className="row g-0">
        {libraries.map((library, index) => (
          <LibraryCard
            key={library.id}
            library={library}
            index={index}
            onOpen={handleOpenLibrary}
          />
        ))}
      </div>

      <SubscriptionModal
        library={subscribeModal}
        onClose={() => setSubscribeModal(null)}
        onSubscribe={handleSubscribe}
        loading={paymentLoading}
      />
      <DynamicLibraryModal
        pkg={dynamicPackage}
        selectedIds={selectedLibraryIds}
        onToggle={(libraryId) => setSelectedLibraryIds((current) => (
          current.includes(libraryId)
            ? current.filter((id) => id !== libraryId)
            : [...current, libraryId]
        ))}
        onClose={() => {
          if (!paymentLoading) setDynamicPackage(null);
        }}
        onConfirm={() => handleSubscribe(dynamicPackage, selectedLibraryIds)}
        loading={paymentLoading}
        error={paymentError}
      />
    </>
  );
};

export default FitnessTraining;
